/**
 * Compresión de imágenes antes de incrustarlas en el HTML.
 *
 * Aquí las imágenes viajan **dentro** del contenido como data URI (no hay
 * almacenamiento de ficheros), así que una foto de móvil de 4 MB se convierte en
 * ~5,4 MB de texto base64 dentro del documento: la fila de la base de datos se
 * dispara, la petición tarda y el editor va a tirones. Antes de pegarla se
 * reduce a un tamaño razonable.
 *
 * Se hace con `canvas` del navegador —redimensionar y recodificar—, que es lo
 * mismo que aplican las librerías del estilo de Compressor.js. No se añade
 * dependencia: esta librería se publica y su valor está en no arrastrar peso.
 *
 * Los SVG se dejan intactos: ya son texto, pesan poco y rasterizarlos sólo
 * empeoraría la calidad.
 */

export interface CompressOptions {
  /** Lado mayor permitido, en píxeles. */
  maxSize?: number
  /** Peso al que se intenta llegar, en bytes. */
  targetBytes?: number
  /** Calidad inicial de la recodificación (0-1). */
  quality?: number
  /** Calidad por debajo de la cual no se baja, para no destrozar la imagen. */
  minQuality?: number
}

export interface CompressResult {
  /** Data URI listo para el `src`. */
  dataUrl: string
  /** Bytes aproximados del data URI resultante. */
  bytes: number
  /** Bytes del archivo original. */
  originalBytes: number
  width: number
  height: number
  /** Falso cuando la imagen se dejó tal cual (SVG, o ya era pequeña). */
  compressed: boolean
}

/**
 * Niveles de compresión ofrecidos en el diálogo de imagen.
 *
 * El equilibrado es el que se usa por defecto: 1280 px de lado mayor y ~120 KB
 * bastan para pantalla y para el PDF, que es donde acaban estas imágenes. El
 * nivel "máxima" existe para documentos con muchas fotos, donde lo que importa
 * es que la fila no se dispare.
 */
export type CompressLevelType = 'alta' | 'equilibrada' | 'maxima'

export const COMPRESS_LEVELS: Record<CompressLevelType, Required<CompressOptions> & { title: string; hint: string }> = {
  alta: {
    title: 'Más calidad',
    hint: 'Hasta 1600 px · ~250 KB',
    maxSize: 1600,
    targetBytes: 250_000,
    quality: 0.85,
    minQuality: 0.5
  },
  equilibrada: {
    title: 'Equilibrada',
    hint: 'Hasta 1280 px · ~120 KB',
    maxSize: 1280,
    targetBytes: 120_000,
    quality: 0.78,
    minQuality: 0.38
  },
  maxima: {
    title: 'Más ligera',
    hint: 'Hasta 1024 px · ~50 KB',
    maxSize: 1024,
    targetBytes: 50_000,
    quality: 0.7,
    minQuality: 0.28
  }
}

const DEFAULTS: Required<CompressOptions> = {
  maxSize: COMPRESS_LEVELS.equilibrada.maxSize,
  targetBytes: COMPRESS_LEVELS.equilibrada.targetBytes,
  quality: COMPRESS_LEVELS.equilibrada.quality,
  minQuality: COMPRESS_LEVELS.equilibrada.minQuality
}

/** Opciones de un nivel, listas para pasar a `compressImage`. */
export const levelOptions = (level: CompressLevelType): CompressOptions => {
  const { maxSize, targetBytes, quality, minQuality } = COMPRESS_LEVELS[level]
  return { maxSize, targetBytes, quality, minQuality }
}

/** Tamaño real de un data URI: base64 crece un tercio sobre el binario. */
export const dataUrlBytes = (dataUrl: string): number => {
  const coma = dataUrl.indexOf(',')
  if (coma === -1) return dataUrl.length
  const base64 = dataUrl.slice(coma + 1)
  const relleno = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return Math.round((base64.length * 3) / 4) - relleno
}

/** "1,8 MB", "210 KB": para contarle al usuario lo que se ahorró. */
export { formatBytes } from '~/utils/status'

const readAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('No pudimos leer la imagen'))
    reader.readAsDataURL(file)
  })

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('La imagen no se pudo abrir'))
    img.src = src
  })

const canvasToDataUrl = (canvas: HTMLCanvasElement, type: string, quality: number): string => canvas.toDataURL(type, quality)

/** ¿El navegador sabe escribir WebP? Pesa bastante menos que JPEG y mantiene el alfa. */
let webpSupport: boolean | null = null
const supportsWebp = (): boolean => {
  if (webpSupport !== null) return webpSupport
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp')
  return webpSupport
}

/**
 * Reduce una imagen a `maxSize` de lado mayor y la recodifica bajando la calidad
 * hasta acercarse a `targetBytes`. Devuelve el data URI y cuánto ocupa.
 */
export async function compressImage(file: File, options: CompressOptions = {}): Promise<CompressResult> {
  const config = { ...DEFAULTS, ...options }
  const originalBytes = file.size

  // Los vectores no se tocan.
  if (file.type === 'image/svg+xml') {
    const dataUrl = await readAsDataUrl(file)
    return { dataUrl, bytes: dataUrlBytes(dataUrl), originalBytes, width: 0, height: 0, compressed: false }
  }

  const original = await readAsDataUrl(file)
  const img = await loadImage(original)

  const escala = Math.min(1, config.maxSize / Math.max(img.naturalWidth, img.naturalHeight))
  const width = Math.max(1, Math.round(img.naturalWidth * escala))
  const height = Math.max(1, Math.round(img.naturalHeight * escala))

  // Ya es pequeña y no hay que redimensionar: se deja como está.
  if (escala === 1 && originalBytes <= config.targetBytes) {
    return { dataUrl: original, bytes: dataUrlBytes(original), originalBytes, width, height, compressed: false }
  }

  // El PNG con transparencia pierde el alfa al pasar a JPEG: sobre fondo blanco
  // se ve raro, así que se prefiere WebP, que lo conserva y además pesa menos.
  const tipo = supportsWebp() ? 'image/webp' : 'image/jpeg'

  /** Dibuja a un tamaño dado y baja la calidad hasta acercarse al objetivo. */
  const render = (w: number, h: number): { dataUrl: string; bytes: number } => {
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return { dataUrl: original, bytes: dataUrlBytes(original) }

    ctx.imageSmoothingQuality = 'high'
    if (tipo === 'image/jpeg') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, w, h)
    }

    // Reducir de golpe a menos de la mitad deja la imagen sucia: se baja por
    // pasos de 50 %, que es como se conserva el detalle al encoger mucho.
    let fuente: CanvasImageSource = img
    let anchoActual = img.naturalWidth
    let altoActual = img.naturalHeight
    while (anchoActual / 2 > w) {
      const medio = document.createElement('canvas')
      medio.width = Math.max(w, Math.round(anchoActual / 2))
      medio.height = Math.max(h, Math.round(altoActual / 2))
      const mctx = medio.getContext('2d')
      if (!mctx) break
      mctx.imageSmoothingQuality = 'high'
      mctx.drawImage(fuente, 0, 0, medio.width, medio.height)
      fuente = medio
      anchoActual = medio.width
      altoActual = medio.height
    }

    ctx.drawImage(fuente, 0, 0, w, h)

    let quality = config.quality
    let url = canvasToDataUrl(canvas, tipo, quality)
    let size = dataUrlBytes(url)
    while (size > config.targetBytes && quality > config.minQuality) {
      quality = Math.max(config.minQuality, quality - 0.1)
      url = canvasToDataUrl(canvas, tipo, quality)
      size = dataUrlBytes(url)
    }
    return { dataUrl: url, bytes: size }
  }

  let salida = render(width, height)
  let finalWidth = width
  let finalHeight = height

  // Con imágenes muy detalladas la calidad mínima no basta para llegar al peso
  // objetivo. Entonces se recorta el tamaño: más vale un poco menos de píxeles
  // que medio megabyte de base64 dentro del documento.
  const escalones = [0.8, 0.65, 0.5, 0.4]
  for (const factor of escalones) {
    if (salida.bytes <= config.targetBytes) break
    const w = Math.max(320, Math.round(width * factor))
    const h = Math.max(200, Math.round(height * factor))
    const intento = render(w, h)
    if (intento.bytes < salida.bytes) {
      salida = intento
      finalWidth = w
      finalHeight = h
    }
  }

  const dataUrl = salida.dataUrl
  const bytes = salida.bytes

  // Si aun así pesa más que el original (pasa con capturas planas en PNG), se
  // devuelve el original: comprimir no puede empeorar el resultado.
  if (bytes >= originalBytes && escala === 1) {
    return { dataUrl: original, bytes: dataUrlBytes(original), originalBytes, width, height, compressed: false }
  }

  return { dataUrl, bytes, originalBytes, width: finalWidth, height: finalHeight, compressed: true }
}
